import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header.jsx";
import ActionButton from "../../components/ActionButton";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config.js";
import { READ_COMPANY_ADDRESS_BY_ID } from "../../graph/companyAddresses/queries.js";
import {
  UPDATE_COMPANY_ADDRESS,
  ARCHIVE_COMPANY_ADDRESS,
} from "../../graph/companyAddresses/mutations.js";

const client = new GraphQLClient(graphqlEndpoint);

const validationSchema = yup.object().shape({
  label: yup.string().nullable(),
  addressLine1: yup.string().trim().required("Address line 1 is required"),
  addressLine2: yup.string().nullable(),
  town: yup.string().nullable(),
  county: yup.string().nullable(),
  postcode: yup.string().nullable(),
  country: yup.string().trim().required("Country is required"),
  latitude: yup
    .number()
    .typeError("Latitude must be a number")
    .min(-90, "Latitude must be between -90 and 90")
    .max(90, "Latitude must be between -90 and 90")
    .nullable(),
  longitude: yup
    .number()
    .typeError("Longitude must be a number")
    .min(-180, "Longitude must be between -180 and 180")
    .max(180, "Longitude must be between -180 and 180")
    .nullable(),
  isPrimary: yup.boolean(),
});

const EditCompanyAddressForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();

  const [initialValues, setInitialValues] = useState(null);

  const redirectPath = location.state?.redirectPath || "/manageCompanies";

  const floatOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;

    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  useEffect(() => {
    const fetchAddress = async () => {
      try {
        const { readCompanyAddressByID } = await client.request(
          READ_COMPANY_ADDRESS_BY_ID,
          { id }
        );

        if (!readCompanyAddressByID) {
          setSnackbar({
            open: true,
            severity: "error",
            message: "Address not found",
          });
          navigate(redirectPath);
          return;
        }

        setInitialValues({
          companyID: readCompanyAddressByID.companyID || "",
          label: readCompanyAddressByID.label || "",
          addressLine1: readCompanyAddressByID.addressLine1 || "",
          addressLine2: readCompanyAddressByID.addressLine2 || "",
          town: readCompanyAddressByID.town || "",
          county: readCompanyAddressByID.county || "",
          postcode: readCompanyAddressByID.postcode || "",
          country: readCompanyAddressByID.country || "United Kingdom",
          latitude: readCompanyAddressByID.latitude ?? "",
          longitude: readCompanyAddressByID.longitude ?? "",
          isPrimary: readCompanyAddressByID.isPrimary ?? false,
        });
      } catch (err) {
        console.error("Error fetching company address:", err);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load address",
        });
      }
    };

    fetchAddress();
  }, [id, navigate, redirectPath, setSnackbar]);

  const handleArchive = async () => {
    if (!window.confirm("Archive this address?")) return;

    try {
      await client.request(ARCHIVE_COMPANY_ADDRESS, { id: String(id) });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Address archived successfully",
      });

      navigate(redirectPath, { state: { refresh: true } });
    } catch (err) {
      console.error("Archive address failed:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to archive address",
      });
    }
  };

  const handleFormSubmit = async (values) => {
    const cleanedValues = { ...values };

    Object.keys(cleanedValues).forEach((key) => {
      if (cleanedValues[key] === "") cleanedValues[key] = null;
    });

    cleanedValues.id = id;
    delete cleanedValues.companyID;

    cleanedValues.latitude = floatOrNull(cleanedValues.latitude);
    cleanedValues.longitude = floatOrNull(cleanedValues.longitude);

    try {
      await client.request(UPDATE_COMPANY_ADDRESS, {
        input: cleanedValues,
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Address updated successfully",
      });

      navigate(redirectPath, { state: { refresh: true } });
    } catch (err) {
      console.error("Update address failed:", err?.response?.errors || err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update address",
      });
    }
  };

  if (!initialValues) return <div>Loading...</div>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="EDIT ADDRESS"
          subtitle={`Edit address for company ${initialValues.companyID}`}
        />

        <Tooltip title="Archive">
          <ActionButton
            tone="danger"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleArchive}
          >
            Archive Address
          </ActionButton>
        </Tooltip>
      </Box>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="Label"
                name="label"
                value={values.label}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.label && !!errors.label}
                helperText={touched.label && errors.label}
                sx={{ gridColumn: "span 2" }}
              />

              <Box sx={{ gridColumn: "span 2" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isPrimary}
                      onChange={(event) =>
                        setFieldValue("isPrimary", event.target.checked)
                      }
                    />
                  }
                  label="Primary address"
                />
              </Box>

              <TextField
                label="Address Line 1"
                name="addressLine1"
                value={values.addressLine1}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.addressLine1 && !!errors.addressLine1}
                helperText={touched.addressLine1 && errors.addressLine1}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                label="Address Line 2"
                name="addressLine2"
                value={values.addressLine2}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.addressLine2 && !!errors.addressLine2}
                helperText={touched.addressLine2 && errors.addressLine2}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                label="Town / City"
                name="town"
                value={values.town}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.town && !!errors.town}
                helperText={touched.town && errors.town}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="County"
                name="county"
                value={values.county}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.county && !!errors.county}
                helperText={touched.county && errors.county}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Postcode"
                name="postcode"
                value={values.postcode}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.postcode && !!errors.postcode}
                helperText={touched.postcode && errors.postcode}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Country"
                name="country"
                value={values.country}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.country && !!errors.country}
                helperText={touched.country && errors.country}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Latitude"
                name="latitude"
                type="number"
                value={values.latitude}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.latitude && !!errors.latitude}
                helperText={touched.latitude && errors.latitude}
                inputProps={{ step: "any" }}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Longitude"
                name="longitude"
                type="number"
                value={values.longitude}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.longitude && !!errors.longitude}
                helperText={touched.longitude && errors.longitude}
                inputProps={{ step: "any" }}
                sx={{ gridColumn: "span 2" }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" gap="10px" mt="20px">
              <ActionButton tone="ghost" onClick={() => navigate(redirectPath)}>
                Cancel
              </ActionButton>

              <ActionButton type="submit" tone="success">
                Save Changes
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditCompanyAddressForm;